/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { FormFieldClickable, FormFieldIcon } from "@insite/mobius/FormField/FormField";
import borderByState, { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { IconWrapper } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import { xBaseTheme } from "@monsma/XDL/types";
import Color from "color";
import { css } from "styled-components";

sizeVariantValues.default = {
    fontSize: 15,
    height: 46,
    icon: 20,
    iconPadding: 10,
    descriptionLineSpacing: 6,
    borderRadius: 2,
    datePickerWidth: 280,
    leftLabelPadding: 10,
    button: "large",
    labelHeight: 18,
};

sizeVariantValues.small = {
    fontSize: 13,
    height: 33,
    icon: 16,
    iconPadding: 10,
    descriptionLineSpacing: 6,
    borderRadius: 2,
    datePickerWidth: 280,
    leftLabelPadding: 10,
    button: "medium",
    labelHeight: 18,
};

const iconPadding = css`
    padding-right: 40px;
    &:last-child {
        padding-right: 10px;
    }
`;

const styleInputs = (props: any) => {
    return css`
        border: 1px solid ${resolveColor("common.ui9", props.theme)};
        border-radius: 2px;
        background-color: ${resolveColor("common.ui7", props.theme)};
        box-shadow: inset 0 1px 0 0 rgba(0, 0, 0, 0.1);
        line-height: 18px;
        ${iconPadding}
        ${
            props.error &&
            css`
            &:not(:disabled) {
                ${borderByState(props, "inactive")}
                border: 1px solid ${resolveColor("danger", props.theme)};
                border-radius: 2px;
                padding: 0 10px;
                & ~ ${IconWrapper as any} {
                  color: ${resolveColor("danger", props.theme)};
                }
            }
        `
        }
        &:not(:disabled) {
            &:hover {
                border-color: ${resolveColor("common.ui3", props.theme)};
            }
            &:focus,
            .focus-within,
            &:focus-within,
            &:active,
            &.react-datetime-picker--open {
              border-color: ${resolveColor("common.ui4", props.theme)};
              border-width: 1px;
              border-radius: 2px;
              padding: 0 10px;
              ${iconPadding}
            }
        }
        &:disabled {
            color: ${getColor("text")};
            opacity: 0.33;
            background: ${getColor("common.ui7")};
            ${IconWrapper as any}, & ~ button ${IconWrapper as any}, & ~ ${IconWrapper as any}{
              background: transparent;
              opacity: 0.33; 
          }
        }
    `;
};

export default {
    defaultProps: {
        border: "rectangle",
        sizeVariant: "default",
        cssOverrides: {
            formInputWrapper: css`
                ${FormFieldClickable as any} {
                    &:hover {
                        ${/* sc-selector */ FormFieldIcon} {
                            color: ${getColor("common.ui3")};
                        }
                    }
                    &:focus,
                    &:active {
                        ${/* sc-selector */ FormFieldIcon} {
                            color: ${getColor("common.ui2")};
                        }
                    }
                }
                .react-datetime-picker {
                    border-color: ${getColor("common.ui9")} !important;
                    height: ${({ _sizeVariant }: any) => (sizeVariantValues as any)[_sizeVariant].height}px;
                }
            `,
            formField: css`
                line-height: 0;
                ${(props: any) => {
                    if (props.disabled) {
                        return `label{color:${Color(resolveColor("text", props.theme)).rgb().alpha(0.33)}}`;
                    }
                    if (props.error) {
                        return `
                      label{color:${resolveColor("danger", props.theme)}};
                      `;
                    }
                }}
                textarea {
                    min-height: 60px;
                    max-width: 100%;
                    width: 100% !important /*Override Inline style on resize*/;
                    ${styleInputs}
                    &:focus-within, &:not(:disabled), &:disabled, &:focus:not(:disabled), &:active:not(:disabled) {
                        padding-top: 10px;
                        padding-bottom: 10px;
                    }
                }

                div[id*=tokenEx],
                div[id*=TokenEx] {
                    iframe {
                        margin: -6px;
                        height: 58px; 
                        width: calc(100% + 12px); 
                    }
                }

            `,
            inputSelect: css`
                word-break: initial;
                overflow-wrap: initial;
                ${styleInputs}
                &.react-datetime-picker {
                    height: ${({ _sizeVariant }: any) => (sizeVariantValues as any)[_sizeVariant].height}px;
                }
            `,
        },
        labelProps: {
            size: 12,
            lineHeight: "18px",
            css: css`
                min-height: 0;
                width: auto;
            `,
        },
        errorProps: {
            size: 13,
            lineHeight: "22px",
            weight: "bold",
            css: css`
                margin-top: 4px;
            `,
        },
        hintProps: {
            size: 13,
            lineHeight: "22px",
            weight: "bold",
            css: css`
                margin-top: 4px;
            `,
        },
        iconProps: {
            color: "common.ui10",
        },
    },
} as xBaseTheme["formField"];
